/**
 * RA2 Art Generator Tool - Specific functionality
 * Extends the shared tools functionality with RA2-specific features
 */

// Tool-specific configuration
const RA2_CONFIG = {
  apiEndpoint: '/api/ra2',
  creditCost: 4,
  creditCostUSD: '0.04',
  maxRetries: 10,
  retryDelay: 1000,
  defaultAspect: 'square',
  aspects: {
    square: { width: 1024, height: 1024, key: '1024x1024' },
    landscape: { width: 1152, height: 768, key: '1152x768' },
    portrait: { width: 768, height: 1152, key: '768x1152' },
    landscape43: { width: 1152, height: 864, key: '1152x864' },
    portrait43: { width: 864, height: 1152, key: '864x1152' },
    widescreen: { width: 1360, height: 768, key: '1360x768' },
    tall: { width: 768, height: 1360, key: '768x1360' },
  },
};

// RA2-specific state
let currentAspect = RA2_CONFIG.defaultAspect;
let generationInProgress = false;
let currentRetry = 0;

function requireToolLogin() {
  if (NetwrckTools && typeof NetwrckTools.requireLogin === 'function') {
    return NetwrckTools.requireLogin();
  }
  return !!(
    window.userData &&
    window.userData.secret &&
    window.userData.secret !== 'anonymous'
  );
}

/**
 * Initialize RA2 Art Generator
 */
export function initializeRA2() {
  if (typeof jQuery === 'undefined' || typeof NetwrckTools === 'undefined') {
    setTimeout(initializeRA2, 100);
    return;
  }

  $(document).ready(function () {
    setupAspectRatioHandlers();
    setupGenerateButton();
    setupKeyboardShortcuts();
    loadSavedPreferences();
    trackToolUsage();
  });
}

/**
 * Setup aspect ratio selection handlers
 */
function setupAspectRatioHandlers() {
  $('input[name="aspect"]').on('change', function () {
    const aspectId = $(this).attr('id').replace('aspect', '');
    currentAspect = aspectId;
    updateResolutionDisplay(aspectId);
    savePreference('lastAspect', aspectId);
  });
}

/**
 * Update resolution display
 */
function updateResolutionDisplay(aspectId) {
  const aspect = getAspectConfig(aspectId);
  if (aspect) {
    console.log(`Aspect ratio changed to: ${aspect.width}x${aspect.height}`);
  }
}

/**
 * Setup generate button and prompt field
 */
function setupGenerateButton() {
  // Click handler is bound in the inline template script -- skip duplicate binding here

  // Auto-save prompt as draft
  let saveTimeout;
  $('#createra2prompt').on('input', function () {
    clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      savePreference('draftPrompt', $(this).val());
    }, 1000);
  });
}

/**
 * Generate RA2 Art
 */
async function generateRA2Art() {
  if (generationInProgress) {
    console.log('Generation already in progress');
    return;
  }

  if (!requireToolLogin()) {
    if (
      window.showSubscribeDialog &&
      typeof window.showSubscribeDialog === 'function'
    ) {
      window.showSubscribeDialog();
    }
    return;
  }

  if (
    !NetwrckTools.checkCredits(RA2_CONFIG.creditCost, RA2_CONFIG.creditCostUSD)
  ) {
    return;
  }

  const prompt = $('#createra2prompt').val().trim();
  if (!prompt) {
    if (typeof window.showToast === 'function') {
      window.showToast('Please enter a description for your scene.', {
        type: 'warning',
      });
    } else {
      alert('Please enter a description for your scene.');
    }
    $('#createra2prompt').focus();
    return;
  }

  generationInProgress = true;
  currentRetry = 0;

  NetwrckTools.showLoading('create-ai-loading-progress');

  try {
    const result = await generateWithRetry(prompt);
    if (result && result.image_url) {
      displayGeneratedImage(result.image_url, prompt);
      trackSuccessfulGeneration(prompt);
      clearDraftPrompt();
      updateCreditsAfterGeneration();
    }
  } catch (error) {
    if (error && error.paywall) return;
    console.error('Generation failed:', error);
    if (typeof window.showToast === 'function') {
      window.showToast('An error occurred. Please try again.', {
        type: 'error',
      });
    } else {
      alert('An error occurred. Please try again.');
    }
    trackFailedGeneration(error);
  } finally {
    generationInProgress = false;
    NetwrckTools.hideLoading('create-ai-loading-progress');
  }
}

/**
 * Generate with retry logic
 */
async function generateWithRetry(prompt) {
  const aspect = getAspectConfig(currentAspect) || RA2_CONFIG.aspects.square;

  // Resolve auth credentials (prefer centralized helper if available)
  const creds =
    (typeof window.getAuthCredentials === 'function'
      ? window.getAuthCredentials()
      : {}) || {};
  const secret = creds.secret || window.userData?.secret || null;
  if (!secret || secret === 'anonymous') {
    NetwrckTools.requireLogin();
    throw new Error('Authentication required');
  }

  const requestData = {
    size: aspect.key,
    prompt: prompt,
    api_key: secret,
    secret: secret,
  };

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key': secret,
      Authorization: `Bearer ${secret}`,
    };

    const response = await fetchRA2WithFallback(headers, requestData);

    const result = await response.json().catch(() => ({}));

    if (window.NetwrckPaywall && window.NetwrckPaywall.check(response, result, { feature: 'RA2 art generation' })) {
      throw window.NetwrckPaywall.error();
    }

    if (response.ok === false) {
      if (response.status >= 500 && currentRetry < RA2_CONFIG.maxRetries) {
        currentRetry++;
        console.log(`Retrying (${currentRetry}/${RA2_CONFIG.maxRetries})...`);
        await new Promise((resolve) =>
          setTimeout(resolve, RA2_CONFIG.retryDelay)
        );
        return generateWithRetry(prompt);
      }
      throw new Error((typeof result.error === 'string' && result.error) || 'Image generation failed');
    }

    if (result.success === false) {
      throw new Error((typeof result.error === 'string' && result.error) || 'Image generation failed');
    }

    if (result.image_url) {
      return result;
    }

    // Retry logic
    if (currentRetry < RA2_CONFIG.maxRetries) {
      currentRetry++;
      console.log(`Retrying (${currentRetry}/${RA2_CONFIG.maxRetries})...`);
      await new Promise((resolve) =>
        setTimeout(resolve, RA2_CONFIG.retryDelay)
      );
      return generateWithRetry(prompt);
    }

    throw new Error('Max retries exceeded');
  } catch (error) {
    if (currentRetry < RA2_CONFIG.maxRetries) {
      currentRetry++;
      console.log(
        `Retrying due to error (${currentRetry}/${RA2_CONFIG.maxRetries}):`,
        error
      );
      await new Promise((resolve) =>
        setTimeout(resolve, RA2_CONFIG.retryDelay)
      );
      return generateWithRetry(prompt);
    }
    throw error;
  }
}

async function fetchRA2WithFallback(headers, requestData) {
  const endpoints = [RA2_CONFIG.apiEndpoint];
  if (
    typeof window !== 'undefined' &&
    window.location &&
    (window.location.hostname === 'netwrck.com' ||
      window.location.hostname === 'www.netwrck.com')
  ) {
    endpoints.push('https://beta.netwrck.com/api/ra2');
  }

  let lastError;
  for (let index = 0; index < endpoints.length; index++) {
    try {
      const response = await fetch(endpoints[index], {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestData),
      });
      if (
        index === 0 &&
        endpoints.length > 1 &&
        [502, 503, 504].includes(response.status)
      ) {
        continue;
      }
      return response;
    } catch (error) {
      lastError = error;
      if (index === endpoints.length - 1) {
        throw error;
      }
    }
  }
  throw lastError || new Error('Image generation failed');
}

/**
 * Display generated image
 */
function displayGeneratedImage(imageUrl, prompt) {
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const uniqueClass = `ai-generated-ra2-${Date.now()}`;
  const avatarUrl = `/tools/talking-avatar?src=${encodeURIComponent(imageUrl)}&name=${encodeURIComponent('Generated avatar')}&prompt=${encodeURIComponent(prompt || '')}`;
  const imgHtml = `
        <div class="ai-character-item-aiart ai-char-item mdl-cell mdl-cell--stretch mdl-cell--2-col mdl-list__item mdl-list__item--three-line"
             title="${esc(prompt || 'RA2 generated image')}"
             data-download="${uniqueClass}"
             data-url="${esc(imageUrl)}">
            <img loading="lazy" 
                 src="${esc(imageUrl)}" 
                 alt="${esc(prompt || 'RA2 generated image')}"
                 data-prompt="${esc(prompt)}"
                 data-timestamp="${Date.now()}"
                 onerror="this.onerror=null; this.src='https://netwrckstatic.netwrck.com/static/img/netwrck-logo-colord256.webp';">
            <div class="image-actions">
                <a href="${esc(imageUrl)}" target="_blank" class="action-btn" title="View full size">
                    <i class="material-icons">open_in_new</i>
                </a>
                <a href="https://editor.ebank.nz/index.html?imageUrl=${encodeURIComponent(imageUrl)}" 
                   target="_blank" class="action-btn" title="Open in Editor">
                    <i class="material-icons">brush</i>
                </a>
                <a href="${esc(avatarUrl)}" class="action-btn" title="Build talking PNG avatar">
                    <i class="material-icons">record_voice_over</i>
                </a>
                <a class="action-btn download-btn" data-url="${esc(imageUrl)}" data-prompt="${esc(prompt)}" title="Download">
                    <i class="material-icons">download</i>
                </a>
            </div>
        </div>
    `;

  $('#createdRA2List').prepend(imgHtml);

  // Initialize MDL components for new elements
  if (typeof componentHandler !== 'undefined') {
    componentHandler.upgradeDom();
  }

  // Setup download button handler
  $('#createdRA2List').on('click', '.download-btn', function (e) {
    e.preventDefault();
    downloadImage($(this).data('url'), $(this).data('prompt'));
  });

  // Increment counter for subscription check
  if (window.subscribeImageGenCounter !== undefined) {
    window.subscribeImageGenCounter++;
    setCookie('subscribeImageGenCounter', window.subscribeImageGenCounter, 365);
  }
}

/**
 * Download generated image
 */
async function downloadImage(imageUrl, prompt) {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ra2-${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '-')}-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    window.open(imageUrl, '_blank');
  }
}

/**
 * Keyboard shortcuts
 */
function setupKeyboardShortcuts() {
  $(document).on('keydown', function (e) {
    // Ctrl/Cmd + Enter to generate
    if ((e.ctrlKey || e.metaKey) && e.keyCode === 13) {
      e.preventDefault();
      generateRA2Art();
    }

    // Escape to clear prompt
    if (e.keyCode === 27 && $('#createra2prompt').is(':focus')) {
      $('#createra2prompt').val('').blur();
    }
  });
}

/**
 * Load saved preferences
 */
function loadSavedPreferences() {
  // Load last used aspect ratio
  const savedAspect = getPreference('lastAspect');
  if (savedAspect && getAspectConfig(savedAspect)) {
    $(`#${savedAspect}aspect`).prop('checked', true).trigger('change');
    // Update MDL radio button display
    if (typeof componentHandler !== 'undefined') {
      componentHandler.upgradeDom();
    }
  }

  // Load draft prompt
  const draftPrompt = getPreference('draftPrompt');
  if (draftPrompt) {
    $('#createra2prompt').val(draftPrompt);
  }
}

/**
 * Save preference to localStorage
 */
function savePreference(key, value) {
  try {
    localStorage.setItem(`ra2_${key}`, value);
  } catch (e) {
    console.warn('Could not save preference:', e);
  }
}

/**
 * Get preference from localStorage
 */
function getPreference(key) {
  try {
    return localStorage.getItem(`ra2_${key}`);
  } catch (e) {
    console.warn('Could not load preference:', e);
    return null;
  }
}

/**
 * Clear draft prompt
 */
function clearDraftPrompt() {
  try {
    localStorage.removeItem('ra2_draftPrompt');
  } catch {
    // Ignore
  }
}

/**
 * Update credits display after generation
 */
function updateCreditsAfterGeneration() {
  if (window.userData && window.userData.credits !== undefined) {
    // Optimistically update credits
    window.userData.credits = Math.max(
      0,
      (window.userData.credits || 0) - RA2_CONFIG.creditCost
    );
    $('.api-credits-balance-usd').text(
      parseFloat(window.userData.credits).toFixed(2)
    );
    $('.api-credits-balance').text(window.userData.credits);
  }
}

/**
 * Analytics tracking
 */
function trackToolUsage() {
  if (typeof trackEvent === 'function') {
    trackEvent('tool', 'load', 'ra2-art-generator');
  }
}

function trackSuccessfulGeneration(prompt) {
  if (typeof trackEvent === 'function') {
    trackEvent('tool', 'generate-success', 'ra2-art-generator', {
      aspect: currentAspect,
      promptLength: prompt.length,
    });
  }
}

function trackFailedGeneration(error) {
  if (typeof trackEvent === 'function') {
    trackEvent('tool', 'generate-error', 'ra2-art-generator', {
      error: error.message,
    });
  }
}

/**
 * Cookie utility (if not available globally)
 */
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

// Initialize when script loads
initializeRA2();

// Export for testing
export {
  RA2_CONFIG,
  generateRA2Art,
  displayGeneratedImage,
  updateResolutionDisplay,
  loadSavedPreferences,
};

/**
 * Resolve aspect config using explicit whitelist mapping
 */
function getAspectConfig(aspectId) {
  switch (aspectId) {
    case 'square':
      return RA2_CONFIG.aspects.square;
    case 'landscape':
      return RA2_CONFIG.aspects.landscape;
    case 'portrait':
      return RA2_CONFIG.aspects.portrait;
    case 'landscape43':
      return RA2_CONFIG.aspects.landscape43;
    case 'portrait43':
      return RA2_CONFIG.aspects.portrait43;
    case 'widescreen':
      return RA2_CONFIG.aspects.widescreen;
    case 'tall':
      return RA2_CONFIG.aspects.tall;
    default:
      return undefined;
  }
}
